import { parseMoneyAmountSR } from './displayUtils';
import { isOutgoingHandoffSourceView } from './handoffWorkflowUtils';

const formatRsd = (amount) =>
  `${Math.round((Number(amount) || 0) * 100) / 100}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' RSD';

/** Pronađi partnerski kontakt (provizija) za prosleđeni posao kod registrovanog partnera. */
export const resolveHandoffPartnerRecord = (job, partners = [], sourceCompanyId) => {
  if (!job || !sourceCompanyId || !Array.isArray(partners)) return null;

  const sourceMatches = sourceCompanyId && String(job.sourceCompanyId || '') === String(sourceCompanyId);
  const partnerCompanyId =
    (sourceMatches &&
    job.companyId &&
    String(job.companyId) !== String(sourceCompanyId)
      ? String(job.companyId)
      : null) ||
    (sourceMatches &&
    job.handoffStatus === 'pending_acceptance' &&
    job.assignedPartnerCompanyId
      ? String(job.assignedPartnerCompanyId)
      : null) ||
    (job.assignedPartnerCompanyId ? String(job.assignedPartnerCompanyId) : null);

  if (partnerCompanyId) {
    const byLinked = partners.find(
      (p) => p.linkedCompanyId && String(p.linkedCompanyId) === partnerCompanyId
    );
    if (byLinked) return byLinked;
  }

  const assigned = job.assignedTo ? String(job.assignedTo) : '';
  if (assigned.startsWith('partner:')) {
    const partnerId = assigned.replace('partner:', '').trim();
    return partners.find((p) => String(p._id || p.id) === partnerId) || null;
  }

  return null;
};

export const hasHandoffSettlementReport = (job) => {
  const report = job?.report;
  if (!report) return false;
  return (
    (report.serviceCharged != null && report.serviceCharged !== '') ||
    report.totalCost != null ||
    report.profit != null ||
    report.reversiCost != null ||
    report.additionalMaterialsCost != null ||
    report.amortizationCost != null ||
    (Array.isArray(report.usedSpareParts) && report.usedSpareParts.length > 0)
  );
};

/** Tekstualni deo izveštaja partnera (šta je popravljeno, materijali, napomene). */
export const hasPartnerWorkReport = (job) => {
  const report = job?.report;
  if (!report) return false;
  return !!(
    (report.workDescription && String(report.workDescription).trim()) ||
    (report.materialsUsed && String(report.materialsUsed).trim()) ||
    (report.additionalNotes && String(report.additionalNotes).trim())
  );
};

export const isHandoffPartnerReportVisible = (job, currentCompanyId) => {
  if (!job || !currentCompanyId) return false;
  if (!isOutgoingHandoffSourceView(job, currentCompanyId)) return false;
  if (!hasHandoffSettlementReport(job) && !hasPartnerWorkReport(job)) return false;

  const status = job.status;
  return (
    status === 'Finished' ||
    status === 'Completed' ||
    status === 'Archived' ||
    !!job.handoffPartnerClosed ||
    !!job.report?.submittedAt ||
    !!job.report?.completedAt
  );
};

export const isHandoffSettlementVisible = (job, currentCompanyId) => {
  if (!isHandoffPartnerReportVisible(job, currentCompanyId)) return false;
  return hasHandoffSettlementReport(job) || hasPartnerWorkReport(job);
};

export const computeHandoffSettlement = (job, commissionRate = 0) => {
  const report = job?.report || {};
  const serviceCharged = parseMoneyAmountSR(report.serviceCharged);
  const reversiCost = Number(report.reversiCost) || 0;
  const additionalMaterialsCost = Number(report.additionalMaterialsCost) || 0;
  const amortizationCost = Number(report.amortizationCost) || 0;
  const totalCost =
    Number(report.totalCost) ||
    reversiCost + additionalMaterialsCost + amortizationCost;
  const profit =
    report.profit != null && report.profit !== ''
      ? Number(report.profit)
      : serviceCharged - totalCost;

  const rate = Math.min(100, Math.max(0, Number(commissionRate) || 0));
  const partnerShare = (profit * rate) / 100;
  const centralShare = profit - partnerShare;

  return {
    serviceCharged,
    reversiCost,
    additionalMaterialsCost,
    amortizationCost,
    totalCost,
    profit,
    commissionRate: rate,
    partnerShare,
    centralShare,
    formatRsd
  };
};

/** Vrednost za avatar/inicijale partnera u prosleđenim poslovima. */
export const resolveOutgoingHandoffPartnerAssignee = (job, partners = [], sourceCompanyId) => {
  const record = resolveHandoffPartnerRecord(job, partners, sourceCompanyId);
  if (record) {
    const id = record._id || record.id;
    if (id) return `partner:${id}`;
    if (record.companyName) {
      return { companyName: record.companyName, color: record.color || '#F97316' };
    }
  }

  const assigned = job?.assignedTo ? String(job.assignedTo) : '';
  if (assigned.startsWith('partner:')) return assigned;

  return null;
};

export const isOutgoingHandoffEditLocked = (job, currentCompanyId) => {
  if (!job || !currentCompanyId) return false;
  if (!isOutgoingHandoffSourceView(job, currentCompanyId)) return false;
  if (job.handoffDeletedByPartner) return true;
  if (job.handoffSenderArchived) return true;

  const status = job.status;
  if (status === 'Finished' || status === 'Completed') return true;
  if (job.handoffPartnerClosed) return true;

  return false;
};

export { formatRsd };
